import { Slider } from "@/design-system/primitives/slider";
import { cn } from "@/design-system/cn.ts";

/**
 * The console's slider: the vendored primitive, plus the two things every one of
 * them needs.
 *
 * **Only the handles are draggable.** The primitive's default is that pressing
 * anywhere on the track jumps the value there, which costs more than it gives on
 * a phone: the drawer closes by swiping it left, and a swipe that happens to
 * start on a slider's track was being eaten as a value change instead. Taking
 * pointer events off everything but the handles hands those strokes back to the
 * drawer, and leaves the handles behaving exactly as before. Keyboard control is
 * untouched — `pointer-events` says nothing about focus.
 *
 * **The ticks line up with the handle.** They mark the stops the value snaps to,
 * so being a few px out reads as the handle failing to land on them.
 *
 * Passing `low`/`onLowChange` turns it into a two-handle range over the same
 * stops — the budget slider's floor. The handles cannot cross or share a stop
 * (`minStepsBetweenThumbs`): a floor equal to the cap would show an empty band,
 * and Radix would otherwise let the pair swap roles mid-drag.
 */

/** Matches the handle's rendered size; the travel maths depends on it. */
const THUMB_PX = 16;

/**
 * Where the handle's centre sits for stop `i` of `count`.
 *
 * The handle is held inside the track rather than hanging off its ends, so its
 * centre travels between `THUMB_PX / 2` and `width - THUMB_PX / 2` — not from
 * edge to edge. Spacing the ticks evenly across the full width instead is why
 * they agreed in the middle and drifted apart by half a handle at both ends.
 */
function tickOffset(i: number, count: number): string {
  const f = count > 1 ? i / (count - 1) : 0;
  return `calc(${THUMB_PX / 2}px + ${(f * 100).toFixed(4)}% - ${(f * THUMB_PX).toFixed(4)}px)`;
}

export function StopSlider({
  value,
  low,
  min = 0,
  max,
  step = 1,
  onChange,
  onLowChange,
  label,
  valueText,
  accent = "signal",
  stops,
  className,
}: {
  value: number;
  /** The lower handle of a range. Omit for the usual single-handle slider. */
  low?: number;
  min?: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
  onLowChange?: (value: number) => void;
  label: string;
  /** What a screen reader says instead of the raw index. */
  valueText?: string;
  /** Which key the fill and handle take. Marine means "geometry you drew". */
  accent?: "signal" | "marine";
  /** Discrete stops to draw beneath. Omit for a continuous slider. */
  stops?: number;
  className?: string;
}) {
  const ranged = low !== undefined;

  // Written out in full rather than composed: Tailwind reads class names as
  // literals in the source, so an interpolated one is never generated.
  const accented =
    accent === "marine"
      ? "[&_[data-slot=slider-range]]:bg-marine-ink [&_[data-slot=slider-thumb]]:border-marine-ink"
      : "[&_[data-slot=slider-range]]:bg-signal-ink [&_[data-slot=slider-thumb]]:border-signal-ink";
  const tickOn = accent === "marine" ? "bg-marine-ink" : "bg-signal-ink";

  /** Is stop `i` a handle, inside the active band, or outside it? */
  const tickState = (i: number): "at" | "in" | "out" => {
    if (ranged) {
      if (i === low || i === value) return "at";
      return i > low && i < value ? "in" : "out";
    }
    if (i === value) return "at";
    return i < value ? "in" : "out";
  };

  return (
    <div className={className}>
      <Slider
        value={ranged ? [low, value] : [value]}
        min={min}
        max={max}
        step={step}
        minStepsBetweenThumbs={ranged ? 1 : undefined}
        onValueChange={(next) => {
          if (ranged) {
            const [lo, hi] = next;
            if (lo !== undefined && lo !== low) onLowChange?.(lo);
            if (hi !== undefined && hi !== value) onChange(hi);
          } else {
            const [v] = next;
            if (v !== undefined) onChange(v);
          }
        }}
        aria-label={label}
        aria-valuetext={valueText}
        className={cn(
          // The handles are the only targets. See the note above.
          "pointer-events-none [&_[data-slot=slider-thumb]]:pointer-events-auto",
          "[&_[data-slot=slider-track]]:h-1 [&_[data-slot=slider-track]]:bg-ink-600",
          "[&_[data-slot=slider-thumb]]:size-4 [&_[data-slot=slider-thumb]]:bg-ink-900",
          accented,
        )}
      />

      {stops !== undefined && stops > 1 && (
        <div className="relative mt-1.5 h-2" aria-hidden="true">
          {Array.from({ length: stops }, (_, i) => {
            const s = tickState(i);
            return (
              <span
                key={i}
                className={cn(
                  "absolute top-0 w-px transition-all",
                  s === "at"
                    ? cn("h-2", tickOn)
                    : s === "in"
                      ? cn("h-1.5 opacity-40", tickOn)
                      : "h-1 bg-rule-bright",
                )}
                style={{ left: tickOffset(i, stops), transform: "translateX(-50%)" }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
